import { action, makeObservable, observable } from 'mobx';
import React from 'react';

const uuiRegex = new RegExp(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-5][0-9a-f]{3}-[089ab][0-9a-f]{3}-[0-9a-f]{12}$/i);
const sha1Regex = new RegExp(/^[0-9a-f]{40}$/i);

export interface IBreadcrumbItem {
    label: string | string[] | React.JSX.Element;
    item: string | string[];
    customLink?: string | string[];
    notLinkedPart?: string | string[];
}

export default class BreadcrumbStore {
    breadcrumbs: (string | (string | string[])[])[] = [];
    items: IBreadcrumbItem[] = [];
    overspillItems: IBreadcrumbItem[] = [];
    itemsSliced: IBreadcrumbItem[] = [];
    sidenavCollapsed: boolean;
    disabledItems = [
        'analyze',
        'search',
        'discover',
        'file',
        'report',
        'my-settings',
        'Microsoft Defender Antivirus',
        'Ransomware Policy',
        'admin',
        'policies',
        'respond'
    ];
    filteredOutItems = [
        '',
        'list',
        'detail',
        'devices',
        'device-groups',
        'login',
        'dwell-time',
        'users',
        'dashboard',
        'ssh-keys',
        'tokens',
        'groups',
        'global-variables',
        'publish-history',
        'queries',
        'issues',
        'summary',
        'items',
        'download',
        'locations'
    ];
    fullWidthItems = ['alerts', 'tasks'];

    constructor() {
        makeObservable(this, {
            breadcrumbs: observable,
            setBreadcrumbs: action.bound,
            clearBreadcrumbs: action,
            testUuid: action,
            setCollapsedSidenav: action,
            sidenavCollapsed: observable
        });
        this.sidenavCollapsed = false;
    }

    setCollapsedSidenav(collapsedSidenav: boolean) {
        this.sidenavCollapsed = collapsedSidenav;
    }

    setBreadcrumbs = (breadcrumbs: any) => {
        this.breadcrumbs = breadcrumbs.filter((item: string | string[]) => !this.filteredOutItems.includes(item as string));

        this.items = this.breadcrumbs.map((item) => {
            if (item === 'av-submission') {
                return {
                    item: item,
                    label: 'Datto AV File Submission'
                };
            }

            if (!Array.isArray(item)) {
                return {
                    item,
                    label: this.testUuid(item) ? item : item.replace(/-/g, ' ')
                };
            }

            const prepareLabel = () => {
                let label = item[2];

                if (!label) {
                    return item[1];
                }

                if (label?.length > 30) {
                    label = label.slice(0, 10) + '...' + label.slice(-10);
                }

                return (
                    <span>
                        {item[1]}
                        <span style={{ textTransform: 'none' }}>{label}</span>
                    </span>
                );
            };

            return {
                item: item[0],
                label: prepareLabel(),
                customLink: item[3],
                notLinkedPart: item[4]
            };
        });

        this.overspillItems = this.items.length > 7 ? this.items.slice(1, this.items.length - 2) : [];
        this.itemsSliced = this.items.slice(this.items.length > 7 ? this.items.length - 2 : 1, this.items.length);
    };

    clearBreadcrumbs = () => (this.breadcrumbs = []);

    testUuid = (str: string) => uuiRegex.test(str);

    testSha1 = (str: string) => sha1Regex.test(str);

    isItemDisabled = (item: string, idx?: number) =>
        this.disabledItems.includes(item) ||
        (!this.breadcrumbs.includes('detection-rules') &&
            !this.breadcrumbs.includes('alert-suppression') &&
            !this.breadcrumbs.includes('extensions') &&
            !this.breadcrumbs.includes('organizations') &&
            this.testUuid(item) &&
            idx === this.breadcrumbs.length - 3) ||
        (this.breadcrumbs.includes('detection-rules') && this.breadcrumbs[this.breadcrumbs.length - 1][0] === 'edit' && idx === 1) ||
        (this.breadcrumbs.includes('alert-suppression') &&
            (this.breadcrumbs[this.breadcrumbs.length - 1][0] === 'edit' || this.breadcrumbs[this.breadcrumbs.length - 1][0] === 'add') &&
            idx === 1) ||
        (this.breadcrumbs.includes('extensions') && this.breadcrumbs[this.breadcrumbs.length - 1][0] === 'edit' && idx === 1) ||
        (this.breadcrumbs.includes('policy-list') && this.breadcrumbs[this.breadcrumbs.length - 1][0] === 'edit' && idx === 1);
}
